/**
 * EmployeeStatTab – generischer, datenquellen-unabhängiger Mitarbeiter-Statistik-Tab.
 * Rendert KPI-Kacheln + (Modus "all") sortierbare Vergleichstabelle
 * oder (Modus "single") zwei Chart-Karten mit Warnhinweis.
 *
 * Wiederverwendbar für künftige Kategorien (Aufgaben, Tagesgeschäft, …):
 * einfach neues Backend-Endpoint + neue `config` übergeben, gleiche Datenform
 * ({ mode, kpis, comparison, weekly_trend, weekday_chart, warnschwelle }).
 */
import React, { useState, useMemo } from 'react';
import { Card, CardContent } from './ui/card';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import {
  Loader2, ArrowUpDown, ArrowUp, ArrowDown, AlertTriangle, CheckCircle2,
  TrendingUp, Calendar,
} from 'lucide-react';

const WEEKDAY_COLORS = ['#3b82f6', '#6366f1', '#8b5cf6', '#ec4899', '#ef4444', '#f97316', '#eab308'];

function fmt(val, format) {
  if (val === null || val === undefined) return '–';
  if (format === 'euro') return `${Number(val).toFixed(2)} €`;
  if (format === 'pct') return `${Number(val).toFixed(0)} %`;
  return val;
}

function KpiCard({ icon: Icon, label, value, sub, color = '#3b82f6' }) {
  return (
    <Card className="overflow-hidden" data-testid={`stat-kpi-${label}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground font-medium truncate">{label}</p>
            <p className="text-2xl font-bold mt-1 text-foreground">{value}</p>
            {sub && <p className="text-xs text-muted-foreground mt-0.5 truncate">{sub}</p>}
          </div>
          {Icon && (
            <div className="p-2 rounded-xl shrink-0 ml-2" style={{ backgroundColor: `${color}15` }}>
              <Icon className="h-5 w-5" style={{ color }} />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function SortableTable({ columns, rows, defaultSortKey }) {
  const [sortKey, setSortKey] = useState(defaultSortKey);
  const [sortDir, setSortDir] = useState('desc');

  const sorted = useMemo(() => {
    const arr = [...rows];
    arr.sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      const as = String(av ?? ''), bs = String(bv ?? '');
      return sortDir === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as);
    });
    return arr;
  }, [rows, sortKey, sortDir]);

  const handleSort = (key) => {
    if (key === sortKey) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  if (!rows.length) {
    return <p className="text-sm text-muted-foreground py-8 text-center">Keine Daten im Zeitraum</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="stat-comparison-table">
        <thead>
          <tr className="border-b border-border">
            {columns.map(col => {
              const active = col.key === sortKey;
              const Icon = !col.sortable ? null : active ? (sortDir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
              return (
                <th
                  key={col.key}
                  onClick={col.sortable ? () => handleSort(col.key) : undefined}
                  data-testid={`stat-col-${col.key}`}
                  className={`py-2.5 px-2 font-semibold text-xs whitespace-nowrap ${
                    col.align === 'right' ? 'text-right' : 'text-left'
                  } ${col.sortable ? 'cursor-pointer select-none hover:text-blue-600' : ''} ${
                    active ? 'text-blue-600' : 'text-muted-foreground'
                  }`}
                >
                  <span className={`inline-flex items-center gap-1 ${col.align === 'right' ? 'flex-row-reverse' : ''}`}>
                    {col.label}
                    {Icon && <Icon className="h-3 w-3" />}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, i) => (
            <tr key={row.id || i} className="border-b border-border/50 hover:bg-muted/40 transition-colors" data-testid={`stat-row-${i}`}>
              {columns.map(col => (
                <td
                  key={col.key}
                  className={`py-2.5 px-2 ${col.align === 'right' ? 'text-right tabular-nums' : 'text-left'}`}
                >
                  {col.key === 'name' ? (
                    <span className="flex items-center gap-1.5">
                      <span className="font-medium truncate">{row.name}</span>
                      {row.active === false && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground shrink-0">inaktiv</span>
                      )}
                    </span>
                  ) : col.key === 'passt_pct' ? (
                    <span className={`font-medium ${row.passt_pct >= 80 ? 'text-green-600' : row.passt_pct >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                      {fmt(row[col.key], col.format)}
                    </span>
                  ) : (
                    fmt(row[col.key], col.format)
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ChartCard({ title, icon: Icon, color, data, valueKey, valueLabel, unit, highlightWorst }) {
  const maxVal = Math.max(...data.map(d => d[valueKey] || 0), 0);
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="p-1.5 rounded-lg" style={{ backgroundColor: `${color}15` }}>
            <Icon className="h-4 w-4" style={{ color }} />
          </div>
          <h3 className="font-semibold text-sm text-foreground">{title}</h3>
        </div>
        {data.some(d => (d[valueKey] || 0) > 0 || (d.count || 0) > 0) ? (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey={data[0]?.short !== undefined ? 'short' : 'label'} tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip
                formatter={(v) => [`${Number(v).toFixed(2)}${unit || ''}`, valueLabel]}
                labelFormatter={(l) => {
                  const d = data.find(x => x.short === l || x.label === l);
                  return d?.day || l;
                }}
                contentStyle={{ fontSize: 11, borderRadius: 8 }}
              />
              <Bar dataKey={valueKey} name={valueLabel} radius={[4, 4, 0, 0]}>
                {data.map((d, i) => (
                  <Cell
                    key={i}
                    fill={highlightWorst && d[valueKey] === maxVal && maxVal > 0
                      ? '#ef4444'
                      : (data[0]?.short !== undefined ? WEEKDAY_COLORS[i % 7] : color)}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[200px] flex items-center justify-center">
            <p className="text-sm text-muted-foreground">Keine Daten im Zeitraum</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function EmployeeStatTab({ data, loading, config }) {
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Loader2 className="h-10 w-10 animate-spin text-blue-600 mb-3" />
        <p className="text-sm text-muted-foreground">Daten werden geladen…</p>
      </div>
    );
  }
  if (!data) return null;

  const kpis = data.kpis || {};
  const accent = config.accentColor || '#0ea5e9';

  const worstDay = (data.weekday_chart || []).reduce(
    (worst, d) => (d[config.chart.valueKey] > (worst?.[config.chart.valueKey] ?? -1) ? d : worst), null
  );

  return (
    <div className="space-y-4">
      {/* KPI-Kacheln */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {config.kpis.map(kpi => (
          <KpiCard
            key={kpi.key}
            icon={kpi.icon}
            label={kpi.label}
            value={fmt(kpis[kpi.key] ?? 0, kpi.format)}
            sub={kpi.sub}
            color={kpi.color}
          />
        ))}
      </div>

      {data.mode === 'all' ? (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5" />
                „Passt genau" = Differenz innerhalb ±{Number(data.warnschwelle ?? 0).toFixed(2)} € · Spalte zum Sortieren anklicken
              </p>
            </div>
            <SortableTable
              columns={config.columns}
              rows={data.comparison || []}
              defaultSortKey={config.defaultSortKey}
            />
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChartCard
              title="Verlauf (letzte 8 Wochen)"
              icon={TrendingUp} color={accent}
              data={data.weekly_trend || []}
              valueKey={config.chart.valueKey} valueLabel={config.chart.valueLabel} unit={config.chart.unit}
            />
            <ChartCard
              title="Wochentags-Analyse"
              icon={Calendar} color="#f97316"
              data={data.weekday_chart || []}
              valueKey={config.chart.valueKey} valueLabel={config.chart.valueLabel} unit={config.chart.unit}
              highlightWorst
            />
          </div>
          {worstDay && worstDay[config.chart.valueKey] > 0 && (
            <p className="text-xs text-red-600 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              Auffälligster Tag: <strong>{worstDay.day}</strong>
              &nbsp;(Ø {Number(worstDay[config.chart.valueKey]).toFixed(2)}{config.chart.unit || ''}) – genauer prüfen!
            </p>
          )}
        </>
      )}
    </div>
  );
}

export default EmployeeStatTab;
